import { useModelConfig } from '@/hooks/useModelConfig'
import { gridViewManager } from '@/utils/grid'
import {
  ArrowRightOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  OrderedListOutlined,
  WarningFilled,
  WarningOutlined,
} from '@ant-design/icons'
import { ReactComponent as WeekendIcon } from '@assets/CustomIcons/summer.svg'
import { defaultDateDef, defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import {
  addCommasToNumber,
  BBDTag,
  GraviButton,
  GraviGrid,
  Horizontal,
  MaterialIcon,
  NothingMessage,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Button, Drawer } from 'antd'
import moment from 'moment/moment'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

const STORAGE_KEY = 'inventory-history'

export function SwingReportGrid({ headerData, historyByICI, isLoading, isError, isCompact = false }) {
  const gridAPIRef = useRef(null)
  const { updateSetting } = useUserSettings()

  const [deliveryListPayload, setDeliveryListPayload] = useState({
    deliveries: [],
    delivery_volume: null,
    gain_loss: null,
    status: null,
    date: null,
    visible: false,
  })
  const openDeliveriesListDrawer = (data) => {
    setDeliveryListPayload({
      deliveries: data.deliveries,
      delivery_volume: data.delivery_volume,
      gain_loss: data.gain_loss,
      status: data.status,
      date: data.date,
      visible: true,
    })
  }

  const columnDefs = useMemo(() => getColumnDefs(openDeliveriesListDrawer), [])
  const getRowId = useCallback((row) => row?.data?.id, [])
  const agPropsOverrides = useMemo(() => ({ getRowId }), [])

  if (isError) return <NothingMessage title='No Data Available.' message='No rows to show.' />
  if (isCompact) {
    return (
      <>
        <GraviGrid
          controlBarProps={{
            title: 'Swing Report',
          }}
          style={{ width: '100%', height: '100%;' }}
          agPropOverrides={agPropsOverrides}
          columnDefs={columnDefs}
          rowData={historyByICI || []}
          externalRef={gridAPIRef}
          domLayout='normal'
          storageKey='inventory-history-compact'
          loading={isLoading}
          rowGroupPanelShow='onlyWhenGrouping'
          onGridConfigChanged={(config) => updateSetting('gridConfig::inventory-history-compact', config)}
        />
        <DeliveriesListDrawer
          deliveryListPayload={deliveryListPayload}
          setDeliveryListPayload={setDeliveryListPayload}
          headerData={headerData}
        />
      </>
    )
  }
  return (
    <>
      <Vertical
        flex='0 1 auto'
        className='bordered p-3 bg-2'
        style={{ borderRadius: '0px 5px 5px 0 ', height: 'auto' }}
      >
        <Texto style={{ flex: 1 }} category='h5'>
          Swing Report For {headerData?.product}
        </Texto>
        <Texto category='h6' appearance='medium'>
          Site {headerData?.site}
        </Texto>
      </Vertical>

      <GraviGrid
        style={{ width: '100%', height: '100%;' }}
        agPropOverrides={agPropsOverrides}
        columnDefs={columnDefs}
        rowData={historyByICI || []}
        externalRef={gridAPIRef}
        domLayout='normal'
        storageKey={STORAGE_KEY}
        rowGroupPanelShow='onlyWhenGrouping'
        loading={isLoading}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
      <DeliveriesListDrawer
        deliveryListPayload={deliveryListPayload}
        setDeliveryListPayload={setDeliveryListPayload}
        headerData={headerData}
      />
    </>
  )
}

function GainLossTag({ status, gain_loss }) {
  if (status === null || !status) {
    return (
      <Horizontal alignItems='center' justifyContent='right'>
        <Texto className='text-truncate pr-4'>
          {addCommasToNumber(gain_loss, 0)}
          {gain_loss < 0 ? <CaretDownOutlined className='ml-2' /> : <CaretUpOutlined className='ml-1' />}
        </Texto>
      </Horizontal>
    )
  }
  if (status === 'warning') {
    return (
      <Horizontal alignItems='center' justifyContent='right'>
        <BBDTag style={{ width: 'fit-content' }} warning className='text-truncate'>
          {addCommasToNumber(gain_loss, 0)}
          {gain_loss < 0 ? <CaretDownOutlined className='ml-2' /> : <CaretUpOutlined className='ml-1' />}
        </BBDTag>
      </Horizontal>
    )
  }
  if (status === 'error') {
    return (
      <Horizontal alignItems='center' justifyContent='right'>
        <BBDTag style={{ width: 'fit-content', backgroundColor: 'var(--theme-error)' }}>
          <Texto appearance='white' className='text-truncate'>
            <WarningFilled className='mr-2' />
            {addCommasToNumber(gain_loss, 0)}
            {gain_loss < 0 ? <CaretDownOutlined className='ml-2' /> : <CaretUpOutlined className='ml-1' />}
          </Texto>
        </BBDTag>
      </Horizontal>
    )
  }
}

const getColumnDefs = (openDeliveriesListDrawer) => {
  return [
    {
      field: 'status',
      headerName: '',
      maxWidth: 30,
      cellRenderer: (params) => {
        if (params.data.status === 'error') {
          return <WarningFilled style={{ color: '#FF0000' }} />
        }
        return null
      },
    },
    {
      field: 'date',
      headerName: 'Date',
      ...defaultDateDef('date'),
      cellRenderer: ({ data }) => {
        return <DateCellRenderer date={moment(data.date)} />
      },
      sort: 'desc',
      filter: 'agDateColumnFilter',
    },
    {
      field: 'beginning_inventory',
      headerName: 'Beginning',
      ...defaultNumberDef('beginning_inventory', 0),
    },
    {
      headerName: 'Deliveries',
      field: 'delivery_volume',
      ...defaultNumberDef('delivery_volume', 0),
      cellRenderer: DeliveriesListRenderer,
      cellRendererParams: { openDeliveriesListDrawer },
    },
    {
      field: 'sales',
      headerName: 'Sales',
      ...defaultNumberDef('sales', 0),
    },
    {
      field: 'book_end_inventory',
      headerName: 'Book End Inv',
      ...defaultNumberDef('book_end_inventory', 0),
    },
    {
      field: 'physical_end_inventory',
      headerName: 'Physical End Inv',
      ...defaultNumberDef('physical_end_inventory', 0),
    },
    {
      field: 'gain_loss',
      headerName: 'Gain Loss',
      ...defaultNumberDef('gain_loss', 0),
      cellRenderer: ({ data }) => GainLossTag({ ...data }),
    },
  ]
}

function DateCellRenderer({ date }) {
  const forecastDate = moment(date)
  const day = forecastDate.format('dddd')
  const isWeekend = day === 'Sunday' || day === 'Saturday'
  const textAppearance = isWeekend ? 'warning' : 'default'

  return (
    <Texto
      textTransform='capitalize'
      category='p2'
      weight={500}
      style={{ display: 'flex', alignItems: 'center' }}
      appearance={textAppearance}
    >
      {isWeekend ? <WeekendIcon style={{ width: 24, paddingRight: '0.5em' }} /> : ''}
      {formatters.WEEKDAY_MONTH_DATE(forecastDate)}
    </Texto>
  )
}

function DeliveriesListRenderer({ data, openDeliveriesListDrawer }) {
  return (
    <Horizontal alignItems='center' justifyContent='right'>
      <Texto>{addCommasToNumber(data.delivery_volume, 0)}</Texto>
      <Button
        type='link'
        disabled={data.delivery_volume === 0}
        onClick={() => openDeliveriesListDrawer(data)}
        icon={<OrderedListOutlined className='pt-1' />}
        style={{ margin: '0', padding: '0 0 0 1em' }}
        size='small'
      >
        View
      </Button>
    </Horizontal>
  )
}

function DeliveriesListDrawer({ deliveryListPayload, setDeliveryListPayload, headerData }) {
  const { config } = useModelConfig()
  return (
    <Drawer
      title={`Site ${headerData.site} | ${headerData.product} | ${moment(deliveryListPayload.date).format('MMM / D')}`}
      placement='right'
      width={450}
      visible={deliveryListPayload.visible}
      onClose={() => setDeliveryListPayload({ ...deliveryListPayload, visible: false })}
      styles={{ body: { paddingTop: 0 }, footer: { backgroundColor: 'var(--bg-2)', paddingRight: '8px' } }}
      footer={
        <Horizontal verticalCenter justifyContent='space-between'>
          <div>
            {config?.enable_delivery_reconciliation && (
              <Link
                to={`/valuation/delivery_report?${new URLSearchParams({
                  site: headerData.site,
                  date: deliveryListPayload.date,
                })}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <GraviButton
                  color='primary'
                  icon={<MaterialIcon className='pt-1' icon='arrow_right_alt' />}
                  iconPosition='end'
                >
                  View Delivery Report
                </GraviButton>
              </Link>
            )}
          </div>
          <Horizontal alignItems='center' justifyContent='flex-end' gap='.5em'>
            <Texto>Delivered Vol: {addCommasToNumber(deliveryListPayload.delivery_volume, 0)}</Texto>
            <GainLossTag {...deliveryListPayload} />
          </Horizontal>
        </Horizontal>
      }
    >
      <Vertical style={{ height: 'auto', paddingTop: '1em' }}>
        {deliveryListPayload.deliveries.map((delivery, idx) => (
          <>
            <DeliveryCard delivery={delivery} key={idx} />
            <div className='border-div' style={{ margin: '2em' }} />
          </>
        ))}
      </Vertical>
    </Drawer>
  )
}

function DeliveryCard({ delivery }) {
  const hasErrors = delivery.has_errors || delivery.missing_prices
  return (
    <Vertical>
      <Horizontal justifyContent='left' alignItems='center'>
        {hasErrors && (
          <WarningOutlined
            color='var(--theme-error)'
            style={{
              padding: '0 .25em 0 0',
              margin: '0',
              color: 'var(--theme-error)',
            }}
          />
        )}
        <Texto appearance='primary' weight='bold'>
          {moment(delivery.date).format('ddd M/D h:mm A')} {!delivery.order_number && '- Manual Layer'}
        </Texto>
      </Horizontal>
      <Horizontal
        style={{
          margin: '0 .25em',
          padding: '0 .5em',
          borderLeft: `solid 3px ${hasErrors ? 'var(--theme-error)' : 'var(--theme-color-1)'}`,
        }}
      >
        <Vertical style={{ marginRight: '25px' }}>
          <Horizontal justifyContent='space-between'>
            <Texto appearance='medium'>Order #:</Texto>
            <Texto weight='bold' appearance='medium'>
              {delivery.order_number || 'N/A'}
            </Texto>
          </Horizontal>
          <Horizontal justifyContent='space-between'>
            <Texto appearance='medium'>BOL #:</Texto>
            <Texto weight='bold' appearance='medium'>
              {delivery.bol_numbers?.[0] || 'N/A'}
            </Texto>
          </Horizontal>
          <Horizontal justifyContent='space-between'>
            <Texto appearance='medium'>PO:</Texto>
            <Texto weight='bold' appearance='medium'>
              {delivery.po || 'N/A'}
            </Texto>
          </Horizontal>
          {delivery.order_number && (
            <Horizontal justifyContent='flex-start' verticalCenter>
              <Link
                target='_blank'
                to={`/order_movements_page/report?${new URLSearchParams({ order_number: delivery.order_number })}`}
              >
                <Button
                  style={{
                    padding: '0',
                    margin: '0',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  type='link'
                  size='small'
                >
                  View Movement
                  <ArrowRightOutlined />
                </Button>
              </Link>
            </Horizontal>
          )}
        </Vertical>
        <Vertical alignItems='right'>
          <Horizontal justifyContent='space-between'>
            <Texto style={{ color: 'transparent' }}>_</Texto>
          </Horizontal>
          <Horizontal justifyContent='space-between'>
            <Texto appearance='medium'>Delivered:</Texto>
            <Texto weight='bold'>{addCommasToNumber(delivery.volume, 0)}</Texto>
          </Horizontal>
          <Horizontal justifyContent='space-between'>
            <Texto appearance='medium'>Supplier:</Texto>
            <Texto weight='bold' appearance='medium'>
              {delivery.suppliers?.[0]}
            </Texto>
          </Horizontal>
        </Vertical>
      </Horizontal>
    </Vertical>
  )
}
